import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output
} from '@angular/core';
import { Store } from '@ngrx/store';

import { OccupationSeries } from '@gfe/features/components/visualization/occupation/models/occupation.model';

import { EntityLabelsTranslationService } from '@core/services/entity-labels-translation/entity-labels-translation.service';
import { selectIsRouteActionCommentable } from '@core/store/comments-config/comments-config.selectors';
import { occupationRequested } from '@core/store/data-visualization/data-visualization.actions';
import { selectOccupationLoading } from '@core/store/data-visualization/data-visualization.selectors';

import { AnchoredPopupComponent } from '@shared/components/anchored-popup/anchored-popup.component';
import {
  StepCommentsPopoverComponent,
  TimelineStepInfo
} from '@shared/components/step-comments-popover/step-comments-popover.component';
import { TimelineStepPopupService } from '@shared/services/timeline-step-popup/timeline-step-popup.service';

/** Label keys already shown as dedicated Details rows; skipped to avoid duplication. */
const DUP_LABEL_KEYS = new Set(['site', 'duration']);

/**
 * Renders the single timeline step popup, anchored to the clicked task block
 * via the shared AnchoredPopupComponent positioning shell.
 */
@Component({
  selector: 'app-timeline-step-popup-outlet',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AnchoredPopupComponent, StepCommentsPopoverComponent],
  template: `
    @if (state(); as s) {
      <app-anchored-popup
        [anchorEl]="s.anchorEl"
        [width]="300"
        [owner]="popupService"
        (dismiss)="popupService.close()">
        <app-step-comments-popover
          [title]="s.data.name"
          [comments]="popupService.conversation()?.comments ?? []"
          [resolved]="popupService.conversation()?.resolved ?? false"
          [loading]="popupService.loading()"
          [disabled]="popupService.saving() || commentsDisabled()"
          [commentsDisabled]="commentsDisabled()"
          [task]="stepInfo()"
          [occupationSeries]="visibleOccupationSeries()"
          [occupationLoading]="occupationLoading()"
          [occupationMarkWindowMs]="occupationMarkWindowMs()"
          [stopoverHoldsInfrastructure]="stopoverHoldsInfrastructure()"
          (occupationOpened)="onOccupationOpened()"
          (add)="add.emit($event)"
          (editComment)="editComment.emit($event)"
          (removeComment)="removeComment.emit($event)"
          (toggleResolve)="toggleResolve.emit()"
          (dismiss)="popupService.close()" />
      </app-anchored-popup>
    }
  `,
  styles: ``
})
export class TimelineStepPopupOutletComponent {
  protected readonly popupService = inject(TimelineStepPopupService);
  private readonly entityLabelsTranslation = inject(EntityLabelsTranslationService);
  private readonly store = inject(Store);
  protected readonly state = this.popupService.state;

  private readonly isRouteActionCommentable = this.store.selectSignal(
    selectIsRouteActionCommentable
  );

  /** All occupation series currently loaded; narrowed per-step in `visibleOccupationSeries`. */
  readonly occupationSeries = input<OccupationSeries[]>([]);

  /** True for tasks that don't support comments (non-work steps, or ROUTE_ACTION not commentable). */
  protected readonly commentsDisabled = computed(() => {
    const s = this.state();
    if (!s) return false;
    return s.data.kind !== 'work' || !this.isRouteActionCommentable();
  });

  protected readonly occupationLoading = this.store.selectSignal(selectOccupationLoading);

  /**
   * Asks only for the open step's site. The timeline may be showing a hundred routes across
   * dozens of sites, and reading one step is no reason to fetch them all.
   */
  protected onOccupationOpened(): void {
    const siteUid = this.state()?.data.siteUid;
    if (siteUid == null) return;
    this.store.dispatch(occupationRequested({ siteUids: [siteUid] }));
  }

  /** Forwarded so the timeline owns persistence. */
  readonly add = output<string>();
  readonly editComment = output<{ id: string; message: string }>();
  readonly removeComment = output<string>();
  readonly toggleResolve = output<void>();

  /**
   * Series for the open step's site, narrowed to the infrastructures this stopover
   * touches; when it touches none, every infrastructure at the site is shown instead.
   */
  protected readonly visibleOccupationSeries = computed<OccupationSeries[]>(() => {
    const s = this.state();
    const siteUid = s?.data.siteUid;
    if (!s || !siteUid) return [];

    const atSite = this.occupationSeries().filter((series) => series.siteUid === siteUid);

    const stopoverUid = s.data.actionUid;
    if (!stopoverUid) return atSite;

    const touched = atSite.filter((series) =>
      series.steps.some((step) => step.holders.some((h) => h.stopoverUid === stopoverUid))
    );

    return touched.length > 0 ? touched : atSite;
  });

  /** Slices of the charted series this stopover is one of the holders of. */
  private readonly heldSteps = computed(() => {
    const stopoverUid = this.state()?.data.actionUid;
    if (!stopoverUid) return [];
    return this.visibleOccupationSeries()
      .flatMap((series) => series.steps)
      .filter((step) => step.holders.some((h) => h.stopoverUid === stopoverUid));
  });

  /**
   * False when this stopover holds no infrastructure here at all — a real answer, not a
   * missing one. A usage comes from the route's trailer requirements, so a trailer that
   * requires nothing yields an empty list while other stopovers at the same site yield
   * full ones. Unsaid, the reader takes the site's chart for this stop's own.
   */
  protected readonly stopoverHoldsInfrastructure = computed(
    () => this.heldSteps().length > 0
  );

  /**
   * The window to outline on the chart: the interval this stopover actually holds
   * infrastructure for.
   *
   * Not the step's own start and end. Those are its service window, while a usage spans the
   * stopover's whole time inside the site, which is normally wider — so outlining the step
   * would mark a span that lines up with nothing the reader can see. Falls back to the step
   * when it holds nothing, where there is no claim to point at and the outline's only job
   * is to say when the truck was on site.
   */
  protected readonly occupationMarkWindowMs = computed<[number, number] | null>(() => {
    const step = this.state()?.data;
    if (!step) return null;

    const held = this.heldSteps();
    if (held.length === 0) return [step.startDate.getTime(), step.endDate.getTime()];

    return [Math.min(...held.map((s) => s.fromMs)), Math.max(...held.map((s) => s.toMs))];
  });

  /** Maps the open Task to the structural shape the popover's Details tab needs. */
  protected readonly stepInfo = computed<TimelineStepInfo | null>(() => {
    const s = this.state();
    if (!s) return null;
    const t = s.data;
    return {
      name: t.name,
      kind: t.kind,
      startDate: t.startDate,
      endDate: t.endDate,
      siteUid: t.siteUid,
      actionIndex: t.actionIndex,
      actionUid: t.actionUid,
      labels: this.entityLabelsTranslation
        .translateMarkerLabels(
          [...(t.labels ?? [])]
            .filter((l) => !DUP_LABEL_KEYS.has(l.label))
            .sort((a, b) => a.level - b.level)
        )
        .map((l) => ({ label: l.label, value: l.value }))
    };
  });
}
